import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Clock, ArrowDownToLine, ArrowUpFromLine, Save } from 'lucide-react';
import {
  resolveEventStartDateTime,
  resolveEventEndDateTime,
  toLocalInput,
} from '../../lib/eventDateTime';

const START_PRESETS = [
  { label: '−2h',  minutes: 120 },
  { label: '−1h',  minutes: 60 },
  { label: '−30m', minutes: 30 },
  { label: '−15m', minutes: 15 },
];
const END_PRESETS = [
  { label: '+15m', minutes: 15 },
  { label: '+30m', minutes: 30 },
  { label: '+1h',  minutes: 60 },
  { label: '+2h',  minutes: 120 },
];

/**
 * Reusable Staff Access Window with quick presets.
 *
 * Props:
 *   eventDate: "YYYY-MM-DD"
 *   eventStartTime: "HH:mm"
 *   eventEndTime: "HH:mm" (required for END presets)
 *   eventIsMultiDay: bool
 *   eventEndDate: "YYYY-MM-DD" (for multi-day END presets)
 *   startValue / endValue: current datetime-local strings
 *   onChangeStart(v) / onChangeEnd(v)
 */
export default function StaffAccessWindow({
  eventDate, eventStartTime, eventEndTime,
  eventIsMultiDay, eventEndDate,
  startValue, endValue,
  onChangeStart, onChangeEnd,
  onSaveDefault, // optional — when present, shows "Save as default" button
}) {
  const startDt = resolveEventStartDateTime({
    date: eventDate,
    time: eventStartTime,
  });
  const endDt = resolveEventEndDateTime({
    date: eventDate,
    time: eventStartTime,
    end_time: eventEndTime,
    end_date: eventEndDate,
    is_multi_day: eventIsMultiDay,
  });

  const applyStartPreset = (minutes) => {
    if (!startDt) return;
    const dt = new Date(startDt.getTime() - minutes * 60 * 1000);
    onChangeStart(toLocalInput(dt));
  };
  const applyEndPreset = (minutes) => {
    if (!endDt) return;
    const dt = new Date(endDt.getTime() + minutes * 60 * 1000);
    onChangeEnd(toLocalInput(dt));
  };

  const startDisabled = !startDt;
  const endDisabled = !endDt;

  return (
    <div className="bg-[#0A0A0A] border border-[#3E4F66]/30 rounded-lg p-6" data-testid="access-window-section">
      <div className="flex items-start gap-2">
        <Clock className="w-4 h-4 text-[#C6A66B] mt-1 flex-shrink-0" />
        <div>
          <h2 className="text-xl font-semibold text-[#EAE7E1] tracking-tight mb-1">Staff Access Window</h2>
          <p className="text-sm text-[#AFAFAF] mb-4">Optional. Restrict when staff can access check-in. Host always has access.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <Label className="text-xs text-[#AFAFAF] flex items-center gap-1.5">
              <ArrowDownToLine className="w-3 h-3" />Opens at
            </Label>
            <div className="flex gap-1" data-testid="staff-window-start-presets">
              {START_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyStartPreset(p.minutes)}
                  disabled={startDisabled}
                  className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                    startDisabled
                      ? 'border-[#1A1A1A] text-[#333] cursor-not-allowed'
                      : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`staff-window-start-preset-${p.minutes}`}
                  title={startDisabled ? 'Set event date and start time first' : `Open ${p.label} before start`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <Input
            type="datetime-local"
            value={startValue || ''}
            onChange={(e) => onChangeStart(e.target.value)}
            className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]"
            data-testid="access-start-input"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
            <Label className="text-xs text-[#AFAFAF] flex items-center gap-1.5">
              <ArrowUpFromLine className="w-3 h-3" />Closes at
            </Label>
            <div className="flex gap-1" data-testid="staff-window-end-presets">
              {END_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyEndPreset(p.minutes)}
                  disabled={endDisabled}
                  className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
                    endDisabled
                      ? 'border-[#1A1A1A] text-[#333] cursor-not-allowed'
                      : 'border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:border-[#D4AF37]/40'
                  }`}
                  data-testid={`staff-window-end-preset-${p.minutes}`}
                  title={endDisabled ? 'Set event date and end time first' : `Close ${p.label} after end`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <Input
            type="datetime-local"
            value={endValue || ''}
            onChange={(e) => onChangeEnd(e.target.value)}
            className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]"
            data-testid="access-end-input"
          />
        </div>
      </div>

      {(startDisabled || endDisabled) && (
        <p className="text-[10px] text-[#555] mt-3">
          Tip: fill in the event date{eventIsMultiDay ? ' and end date' : ''}, start time and end time above to enable quick presets.
        </p>
      )}

      {onSaveDefault && (() => {
        // Compute positive offsets: pre = minutes BEFORE event start; post = minutes AFTER event end.
        // Matches the existing host_defaults schema (staff_access_pre_start_minutes / post_end_minutes).
        const startVal = startValue ? new Date(startValue) : null;
        const endVal = endValue ? new Date(endValue) : null;
        const preMin = startDt && startVal && !Number.isNaN(startVal.getTime())
          ? Math.round((startDt.getTime() - startVal.getTime()) / 60000)
          : null;
        const postMin = endDt && endVal && !Number.isNaN(endVal.getTime())
          ? Math.round((endVal.getTime() - endDt.getTime()) / 60000)
          : null;
        const canSave = (preMin != null || postMin != null);
        const offsetsOutOfRange =
          (preMin != null && (preMin < -1440 || preMin > 1440)) ||
          (postMin != null && (postMin < -1440 || postMin > 1440));
        const disabled = !canSave || offsetsOutOfRange;
        return (
          <div className="mt-4 pt-4 border-t border-[#1A1A1A] flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11px] text-[#777] flex-1 min-w-0">
              {canSave && !offsetsOutOfRange
                ? `Save these offsets (${preMin != null ? (preMin >= 0 ? `-${preMin}m` : `+${-preMin}m`) : '—'} / ${postMin != null ? (postMin >= 0 ? `+${postMin}m` : `-${-postMin}m`) : '—'}) as your personal default for new events.`
                : 'Set both open and close times to save this window as your default.'}
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onSaveDefault({ pre_min: preMin, post_min: postMin })}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm border transition-colors ${
                disabled
                  ? 'border-[#1A1A1A] text-[#444] cursor-not-allowed'
                  : 'border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70'
              }`}
              data-testid="staff-window-save-default"
              title={offsetsOutOfRange ? 'Offsets must be within ±24h of the event time' : 'Save as default for new events'}
            >
              <Save className="w-3 h-3" />
              Save as default
            </button>
          </div>
        );
      })()}
    </div>
  );
}
